import {ErrorMessage, Field, Form, Formik} from "formik";
import React, {useState} from "react";
import styles from "../../styles/list_styles.module.scss";
import Button from "../../components/Button/Button";

import {ReactComponent as Download} from "../../assets/icons/download.svg";
import {ReactComponent as Pencil} from "../../assets/icons/pencil.svg";
import {ReactComponent as Trash} from "../../assets/icons/trash.svg";

import {BarLoader} from "react-spinners";
import {useNavigate} from "react-router-dom";
import {deleteProduct, filterCategories, filterTeams, getProducts, serializeArray} from "../../services/services";
import Pagination from "../../components/Pagination/Pagination";
import ModalConfirm from "../../components/ModalConfirm/ModalConfirm";
import {useMutation, useQuery} from "react-query";
import {toast} from "react-toastify";
import {queryClient} from "../../services/queryClient";
import {getToken} from "../../services/auth";
import Select2 from "react-select";
import {stylesSelect} from "../../helper/stylesSelect";
import {IGetProducts, IProductsResponse} from "../../ts/interfaces/Product/interface";
import {IFilter} from "../../ts/interfaces/interface";

const Products: React.FC = () => {
    document.title = 'Admin - Produtos';

    const [filter, setFilter] = useState<IGetProducts>({
        id: null,
        name: null,
        sku: null,
        type: null,
        is_active: null,
        team_id: null,
        category_id: null,
        gender: null,
        page: 1,
        limit: 50,
    });

    const [open, setOpen] = useState(false);
    const [id, setId] = useState<number | null>(null);
    const [selectFilters, setSelectFilters] = useState({
        type: "",
        is_active: "",
        team_id: "",
        category_id: "",
        gender: "",
    });

    const navigate = useNavigate();

    const {data, isLoading} = useQuery<IProductsResponse>(
        ["products", filter],
        () => getProducts(filter),
        {
            staleTime: Infinity,
        }
    );

    const handleDownloadCSV = () => {
        let link = document.getElementById("download_csv");
        link?.click();
    };

    const mutateDeleteProduct = useMutation(
        () => {
            return deleteProduct(id as number);
        },
        {
            onSettled: (data) => {
                if (data?.data?.success) {
                    queryClient.invalidateQueries(["products", filter]);
                    toast.success('Deletado com sucesso');
                } else {
                    toast.error("Erro ao deletar produto. " + data?.data?.msg);
                }
                setOpen(false);
            },
            onError: () => {
                toast.error("Erro ao deletar produto.");
            },
        }
    );

    const {data: teams} = useQuery<{ data: IFilter[] }>(
        "filter-teams",
        () => filterTeams()
    );

    const {data: categories} = useQuery<{ data: IFilter[] }>(
        "filter-categories",
        () => filterCategories()
    );

    return (
        <div className={styles.container}>
            <h1>Produtos</h1>

            <Formik
                initialValues={{
                    id: "",
                    name: "",
                    sku: "",
                }}
                onSubmit={(values) => {
                    setFilter({
                        ...filter,
                        id: values.id && values.id !== '' ? parseInt(values.id) : null,
                        name: values.name,
                        sku: values.sku,
                        type: selectFilters.type,
                        is_active: !selectFilters.is_active ? null : selectFilters.is_active === '1',
                        team_id: selectFilters.team_id && selectFilters.team_id !== '' ? parseInt(selectFilters.team_id) : null,
                        category_id: selectFilters.category_id && selectFilters.category_id !== '' ? parseInt(selectFilters.category_id) : null,
                        gender: selectFilters.gender,
                        page: 1,
                    });
                }}
            >
                <Form className={styles.searchProducts}>
                    <div className={styles.alignInput}>
                        <Field
                            name="id"
                            type="text"
                            placeholder="Pesquisar por ID"
                            className={styles.input}
                            disabled={isLoading}
                        />
                        <ErrorMessage
                            name="id"
                            component="div"
                            className={styles.error}
                        />

                        <Field
                            name="name"
                            type="text"
                            placeholder="Pesquisar por nome"
                            className={styles.input}
                            disabled={isLoading}
                        />
                        <ErrorMessage
                            name="name"
                            component="div"
                            className={styles.error}
                        />

                        <Field
                            name="sku"
                            type="text"
                            placeholder="Pesquisar por SKU"
                            className={styles.input}
                            disabled={isLoading}
                        />
                        <ErrorMessage
                            name="sku"
                            component="div"
                            className={styles.error}
                        />

                        <div className={styles.groupInput}>
                            <Select2
                                styles={stylesSelect}
                                options={[
                                    {value: "Camisa", label: "Camisa"},
                                    {value: "Short", label: "Short"},
                                ]}
                                onChange={(selectedOption) => {
                                    const selected = selectedOption?.value || "";
                                    setSelectFilters((prev) => ({...prev, type: selected.toString()}));

                                }}
                                placeholder="Pesquisar por tipo"
                            />
                        </div>

                        <div className={styles.groupInput}>
                            <Select2
                                styles={stylesSelect}
                                options={[
                                    {value: "1", label: "Ativo"},
                                    {value: "0", label: "Desativo"},
                                ]}
                                onChange={(selectedOption) => {
                                    const selected = selectedOption?.value || "";
                                    setSelectFilters((prev) => ({...prev, is_active: selected.toString()}));

                                }}
                                placeholder="Pesquisar por status"
                            />
                        </div>

                        <div className={styles.groupInput}>
                            <Select2
                                styles={stylesSelect}
                                options={teams?.data ?? []}
                                onChange={(selectedOption) => {
                                    const selected = selectedOption?.value || "";
                                    setSelectFilters((prev) => ({...prev, team_id: selected.toString()}));

                                }}
                                placeholder="Pesquisar por time"
                            />
                        </div>

                        <div className={styles.groupInput}>
                            <Select2
                                styles={stylesSelect}
                                options={categories?.data ?? []}
                                onChange={(selectedOption) => {
                                    const selected = selectedOption?.value || "";
                                    setSelectFilters((prev) => ({...prev, category_id: selected.toString()}));

                                }}
                                placeholder="Pesquisar por categoria"
                            />
                        </div>

                        <div className={styles.groupInput}>
                            <Select2
                                styles={stylesSelect}
                                options={[
                                    {value: "Masculino", label: "Masculino"},
                                    {value: "Feminino", label: "Feminino"},
                                    {value: "Unisex", label: "Unisex"},
                                ]}
                                onChange={(selectedOption) => {
                                    const selected = selectedOption?.value || "";
                                    setSelectFilters((prev) => ({...prev, gender: selected.toString()}));

                                }}
                                placeholder="Pesquisar por gênero"
                            />
                        </div>
                    </div>
                    <div className={styles.alignButtonSearch}>
                        <Button
                            type="submit"
                            width="115px"
                            height="39px"
                            theme="primary"
                            fontSize="14px"
                            disabled={isLoading}
                        >
                            Pesquisar
                        </Button>
                    </div>
                </Form>
            </Formik>

            <div className={styles.alignButtonCsvRegister}>
            <Button
                    width="115px"
                    height="39px"
                    theme="close"
                    icon={<Download/>}
                    fontSize="14px"
                    onClick={() => handleDownloadCSV()}
                    disabled={isLoading}
                >
                    CSV
                </Button>
                <Button
                    width="115px"
                    height="39px"
                    theme="primary"
                    fontSize="14px"
                    onClick={() => navigate("/product/create")}
                    disabled={isLoading}
                >
                    Cadastrar
                </Button>
            </div>
            <div className={styles.dNone}>
                <a
                    id="download_csv"
                    rel="noopener noreferrer"
                    href={
                        process.env.REACT_APP_API_URL +
                        "/admin/product/export?" +
                        serializeArray(filter) +
                        "&token=" +
                        getToken()
                    }
                    target="_blank"
                    download
                ></a>
            </div>

            <div className={styles.tableProducts}>
                {isLoading ? (
                    <div className={styles.alignCenter}>
                        <span>Carregando</span>
                        <BarLoader
                            color={"#2b6cded9"}
                            loading={isLoading}
                            aria-label="Loading Spinner"
                            data-testid="loader"
                        />
                    </div>
                ) : (
                    <>
                        {data && data.data.products.length > 0 ? (
                            <table>
                                <thead>
                                <tr>
                                    <th>Nome</th>
                                    <th>SKU</th>
                                    <th>Tipo</th>
                                    <th>Preço</th>
                                    <th>Ativo</th>
                                    <th>Time</th>
                                    <th>Categorias</th>
                                    <th>Gênero</th>
                                    <th>Temporada</th>
                                    <th>Estoque</th>
                                    <th>Dt Cadastro</th>
                                    <th></th>
                                    <th></th>
                                </tr>
                                </thead>
                                <tbody>
                                {data.data.products.map(
                                    (product) => (
                                        <tr key={product.id}>
                                            <td>{product.name}</td>
                                            <td>{product.sku}</td>
                                            <td>{product.type}</td>
                                            <td>{product.special_price ?? product.price}</td>
                                            <td>{product.is_active}</td>
                                            <td>{product.team}</td>
                                            <td>{product.categories}</td>
                                            <td>{product.gender}</td>
                                            <td>{product.season}</td>
                                            <td>{product.stock}</td>
                                            <td>{product.created_at}</td>
                                            <td>
                                                <button
                                                    className={
                                                        styles.editProducts
                                                    }
                                                    onClick={() =>
                                                        navigate(
                                                            `/product/create/${product.id}`
                                                        )
                                                    }
                                                >
                                                    <Pencil/>
                                                </button>
                                            </td>
                                            <td>
                                                <button
                                                    onClick={() => {
                                                        setId(
                                                            product.id
                                                        );
                                                        setOpen(true);
                                                    }}
                                                    className={
                                                        styles.editProducts
                                                    }
                                                >
                                                    <Trash/>
                                                </button>
                                            </td>
                                        </tr>
                                    )
                                )}
                                </tbody>
                            </table>
                        ) : (
                            <div className={styles.alignCenter}>
                                <span>Nenhum registro encontrado.</span>
                            </div>
                        )}
                    </>
                )}
            </div>
            {data && (
                <Pagination
                    currentPage={data.data.currentPage}
                    lastPage={data.data.lastPage}
                    onPageChange={(payload: number) =>
                        setFilter({...filter, page: payload})
                    }
                />
            )}

            {open && id && (
                <ModalConfirm
                    propsFunction={mutateDeleteProduct.mutate}
                    open={open}
                    setOpen={setOpen}
                    loading={mutateDeleteProduct.isLoading}
                >
                    <span>Essa ação é irreversível!</span>
                    <span>Tem certeza que deseja deletar o produto?</span>
                </ModalConfirm>
            )}
        </div>
    );
};

export default Products;
